/**
 * The only place a price is ever computed.
 *
 * Pure and dependency-free on purpose: `createOrder` calls it on the server with
 * the Firestore copy of the config, and the order form calls it in the browser
 * with the same config to draw the total. One implementation means the number on
 * the button and the number Beam charges cannot drift apart.
 *
 * Nothing here accepts a price from its caller.
 */
import { DELIVERY_SHIP, EVENT_OPEN, SATANG_PER_BAHT } from './constants.js'
import { appError } from './errors.js'
import type { ColorOption, EventConfig, EventItem, SizeOption } from './schemas/event.js'
import type { Delivery, OrderLine, OrderLineInput } from './schemas/order.js'

export const findItem = (config: EventConfig, sku: string): EventItem | undefined =>
  config.items.find((item) => item.sku === sku)

export const findSize = (item: EventItem, code: string): SizeOption | undefined =>
  item.sizes.find((size) => size.code === code)

export const findColor = (item: EventItem, code: string | undefined): ColorOption | undefined =>
  code === undefined ? undefined : item.colors.find((color) => color.code === code)

/** An item with no colourways at all — a fee, a membership, a one-colour shirt. */
export const hasColors = (item: EventItem): boolean => item.colors.length > 0

export const unitPriceOf = (item: EventItem, size: SizeOption, color?: ColorOption): number =>
  item.basePrice + size.extra + (color?.extra ?? 0)

/** Cheapest way into the event — the "ราคาเริ่มต้น" figure on the landing card. */
export const minPriceOf = (config: EventConfig): number =>
  Math.min(
    ...config.items.map(
      (item) =>
        item.basePrice +
        Math.min(...item.sizes.map((size) => size.extra)) +
        (hasColors(item) ? Math.min(...item.colors.map((color) => color.extra)) : 0),
    ),
  )

export const requireItem = (config: EventConfig, sku: string): EventItem => {
  const item = findItem(config, sku)
  if (!item) throw appError('item_not_found', `ไม่พบสินค้ารหัส ${sku}`, { sku })
  return item
}

export const requireSize = (item: EventItem, code: string): SizeOption => {
  const size = findSize(item, code)
  if (!size) {
    throw appError('size_not_found', `ไม่พบ size ${code} ของ${item.name}`, { sku: item.sku, size: code })
  }
  return size
}

/**
 * Colour is required exactly when the item has colourways, and refused when it does
 * not — so a client cannot smuggle an unpriced attribute onto a line, and cannot
 * leave the factory guessing which colour to print.
 */
export const requireColor = (item: EventItem, code: string | undefined): ColorOption | undefined => {
  if (!hasColors(item)) {
    if (code !== undefined) {
      throw appError('color_not_found', `${item.name} ไม่มีให้เลือกสี`, { sku: item.sku, color: code })
    }
    return undefined
  }
  if (code === undefined) {
    throw appError('color_required', `กรุณาเลือกสีของ${item.name}`, { sku: item.sku })
  }
  const color = findColor(item, code)
  if (!color) {
    throw appError('color_not_found', `ไม่พบสี ${code} ของ${item.name}`, { sku: item.sku, color: code })
  }
  return color
}

/** Turn one requested line into a priced line. */
export const priceLine = (config: EventConfig, line: OrderLineInput): OrderLine => {
  const item = requireItem(config, line.sku)
  const size = requireSize(item, line.size)
  const color = requireColor(item, line.color)
  const unitPrice = unitPriceOf(item, size, color)
  return {
    sku: item.sku,
    name: item.name,
    ...(color ? { color: color.code, colorName: color.name } : {}),
    size: size.code,
    qty: line.qty,
    unitPrice,
    lineTotal: unitPrice * line.qty,
  }
}

export type PricedOrder = {
  items: OrderLine[]
  /** Total garments, not total lines — this is the number the factory order counts in. */
  itemCount: number
  /** The shirts, BEFORE tax. This is the figure the factory quotes. */
  subtotal: number
  /** 7% of `subtotal`. Shipping is a courier's service, not the factory's supply. */
  vat: number
  shippingFee: number
  amount: number
}

export type PriceOrderInput = {
  lines: readonly OrderLineInput[]
  delivery: Pick<Delivery, 'type'>
}

export const priceOrder = (config: EventConfig, input: PriceOrderInput): PricedOrder => {
  if (input.lines.length === 0) throw appError('empty_order', 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการ')

  const items = mergeLines(input.lines).map((line) => priceLine(config, line))
  const subtotal = items.reduce((sum, line) => sum + line.lineTotal, 0)
  const itemCount = items.reduce((sum, line) => sum + line.qty, 0)

  const wantsShipping = input.delivery.type === DELIVERY_SHIP
  if (wantsShipping && !config.shipping.enabled) {
    throw appError('shipping_disabled', 'งานนี้ไม่มีบริการจัดส่ง กรุณาเลือกรับเองที่บริษัท')
  }
  const shippingFee = wantsShipping ? config.shipping.fee : 0

  /*
   * VAT on the shirts only, added on top — the factory quotes ฿263 "ยังไม่รวมภาษี",
   * so the price in the config is the pre-tax one and the tax is a visible line
   * rather than something buried in a rounded-up number.
   *
   * Shipping is excluded: that is a courier's charge, not part of the factory's
   * supply, and taxing it here would be inventing a figure nobody billed.
   *
   * Rounded to the satang at the point it is computed, so the total is a number that
   * can actually be transferred and every later sum works on settled figures.
   */
  const vat = round2(subtotal * config.vatRate)

  return { items, itemCount, subtotal, vat, shippingFee, amount: round2(subtotal + vat + shippingFee) }
}

/** To the satang, via integers — `0.1 + 0.2` is not `0.3` in binary floating point. */
export const round2 = (amount: number): number => Math.round(amount * 100) / 100

/**
 * Adding money.
 *
 * Totals used to be whole baht, where `+` is exact. Now that tax puts satang on
 * every order, adding them as decimals accumulates error: two orders of ฿321.41 and
 * ฿562.82 sum to ฿884.2300000000001, and a revenue figure that renders as that once
 * in a hundred refreshes is the kind of thing an organiser screenshots.
 *
 * Summed as integers, converted back once at the end.
 */
export const sumBaht = (amounts: readonly number[]): number =>
  amounts.reduce((satang, amount) => satang + Math.round(amount * 100), 0) / 100

/**
 * Can this line still be ordered against this config?
 *
 * The organiser can retire a colour or a size from `/admin` while somebody is
 * halfway through the form, and a restored draft then names a variant that no
 * longer exists. `priceLine` throws on those, which is right for the server and
 * useless for the client — so the same rules are offered as a question here, and
 * the form drops what it can no longer sell instead of rendering an empty order.
 */
export const isOrderableLine = (config: EventConfig, line: OrderLineInput): boolean => {
  const item = findItem(config, line.sku)
  if (!item || !findSize(item, line.size)) return false
  return hasColors(item) ? !!findColor(item, line.color) : !line.color
}

/** The lines still sellable, in order. Empty means the draft has nothing left. */
export const orderableLines = (
  config: EventConfig,
  lines: readonly OrderLineInput[],
): OrderLineInput[] => lines.filter((line) => isOrderableLine(config, line))

/** Identity of a line: the same shirt in the same colour and size is one row. */
export const lineKey = (line: Pick<OrderLineInput, 'sku' | 'color' | 'size'>): string =>
  `${line.sku}::${line.color ?? ''}::${line.size}`

/** Two rows of the same sku+colour+size are one line with a bigger qty. */
export const mergeLines = (lines: readonly OrderLineInput[]): OrderLineInput[] => {
  const byKey = new Map<string, OrderLineInput>()
  for (const line of lines) {
    const key = lineKey(line)
    const found = byKey.get(key)
    byKey.set(key, found ? { ...found, qty: found.qty + line.qty } : { ...line })
  }
  return [...byKey.values()]
}

export const assertEventOpen = (config: EventConfig, now: Date): void => {
  if (config.status !== EVENT_OPEN) throw appError('event_not_open', 'งานนี้ยังไม่เปิดรับออเดอร์')
  if (now.getTime() > config.closeAt.getTime()) throw appError('event_closed', 'ปิดรับออเดอร์แล้ว')
}

export const isEventOpen = (config: EventConfig, now: Date): boolean =>
  config.status === EVENT_OPEN && now.getTime() <= config.closeAt.getTime()

/**
 * Sizes an admin may swap a line to: same item, same colour, different code, and
 * identical unit price. A different price means a different order, not an edit —
 * the amount on the order is what the buyer was already charged.
 */
export const sizeSwapTargets = (
  config: EventConfig,
  sku: string,
  fromSize: string,
  colorCode?: string,
): SizeOption[] => {
  const item = requireItem(config, sku)
  const current = requireSize(item, fromSize)
  const color = findColor(item, colorCode)
  const price = unitPriceOf(item, current, color)
  return item.sizes.filter(
    (size) => size.code !== current.code && unitPriceOf(item, size, color) === price,
  )
}

/** Colourways that cost the same as the one already paid for, at the same size. */
export const colorSwapTargets = (
  config: EventConfig,
  sku: string,
  size: string,
  fromColor: string | undefined,
): ColorOption[] => {
  const item = requireItem(config, sku)
  if (!hasColors(item)) return []
  const sizeOption = requireSize(item, size)
  const current = findColor(item, fromColor)
  const price = unitPriceOf(item, sizeOption, current)
  return item.colors.filter(
    (color) => color.code !== fromColor && unitPriceOf(item, sizeOption, color) === price,
  )
}

export const assertSwappableLine = (
  config: EventConfig,
  line: Pick<OrderLineInput, 'sku' | 'color' | 'size'>,
  to: { size: string; color?: string },
): void => {
  const toColor = to.color ?? line.color
  if (to.size === line.size && toColor === line.color) {
    throw appError('invalid_input', 'size และสีเดิมกับที่เลือกใหม่เหมือนกัน')
  }

  const item = requireItem(config, line.sku)
  const fromPrice = unitPriceOf(item, requireSize(item, line.size), findColor(item, line.color))
  const toSize = findSize(item, to.size)
  if (!toSize) {
    throw appError('size_not_found', `ไม่พบ size ${to.size} ของ${item.name}`, { sku: line.sku })
  }
  const nextColor = hasColors(item) ? findColor(item, toColor) : undefined
  if (hasColors(item) && !nextColor) {
    throw appError('color_not_found', `ไม่พบสี ${toColor} ของ${item.name}`, { sku: line.sku })
  }

  if (unitPriceOf(item, toSize, nextColor) !== fromPrice) {
    throw appError(
      'size_price_mismatch',
      `เปลี่ยนเป็น ${to.size}${nextColor ? ` สี${nextColor.name}` : ''} ไม่ได้เพราะราคาไม่เท่ากัน กรุณาให้ลูกค้าสั่งออเดอร์ใหม่`,
      { sku: line.sku, fromSize: line.size, toSize: to.size },
    )
  }
}

/** Beam counts in satang. This is the only conversion in the codebase. */
export const toSatang = (baht: number): number => Math.round(baht * SATANG_PER_BAHT)
export const fromSatang = (satang: number): number => Math.round(satang / SATANG_PER_BAHT)

